/**
 * LeasingAutomationConfig — „Kontaktstrecke" (Aug-2026, User-Wunsch).
 * Konsistent zu den Finanzierungen: HIER nur Automatik + frei editierbare
 * ZEITPUNKTE je Stufe. Die eigentlichen E-Mail-TEXTE werden zentral unter
 * „Freigabe & Texte" gepflegt (nicht mehr hier).
 * Stufen: Einladung · Erinnerung (2 Zeitpunkte) · Endet heute ·
 * 1–2 Wochen überfällig · 3–4 Wochen überfällig · Sicherstellung.
 */
import { useEffect, useState } from "react";
import { Loader2, Play, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";

const STAGES = [
    { label: "Einladung", hint: "Erstkontakt mit dem Optionen-Link", days: ["days_invite"], tpl: "invite" },
    { label: "Erinnerung (vor Ende)", hint: "zwei Zeitpunkte vor Vertragsende", days: ["days_reminder1", "days_reminder2"], tpl: "reminder" },
    { label: "Endet heute", hint: "am Tag des Vertragsendes", days: ["days_due_today"], tpl: "due_today" },
    { label: "1–2 Wochen überfällig", hint: "nach dem Ende (negative Tage = danach)", days: ["days_overdue1"], tpl: "overdue1" },
    { label: "3–4 Wochen überfällig", hint: "nach dem Ende (negative Tage = danach)", days: ["days_overdue2"], tpl: "overdue2" },
    { label: "Sicherstellung", hint: "letzte Kunden-Mail + interne Aufgabe", days: ["days_securing"], tpl: "securing" },
];
const DAY_LABEL = { days_reminder1: "Erinnerung 1", days_reminder2: "Erinnerung 2" };

export default function LeasingAutomationConfig({ onSaved, onEditTemplate }) {
    const [t, setT] = useState(null);
    const [busy, setBusy] = useState(false);
    const [runBusy, setRunBusy] = useState(false);

    useEffect(() => {
        api.get("/leasing-returns/texts").then((r) => setT({ ...r.data.texts })).catch(() => toast.error("Einstellungen konnten nicht geladen werden."));
    }, []);

    const set = (k, v) => setT((p) => ({ ...p, [k]: v }));

    const save = async () => {
        setBusy(true);
        try { await api.put("/leasing-returns/texts", t); toast.success("Kontaktstrecke gespeichert."); onSaved?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
        finally { setBusy(false); }
    };

    const runNow = async () => {
        setRunBusy(true);
        try {
            const r = await api.post("/leasing-returns/reminders/run-now");
            toast.success(`Lauf abgeschlossen — ${r.data?.processed ?? 0} Vorgang/Vorgänge, ${r.data?.assigned ?? 0} zugewiesen.`);
        } catch (e) { toast.error(e?.response?.data?.detail || "Lauf fehlgeschlagen."); }
        finally { setRunBusy(false); }
    };

    if (!t) return <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;

    return (
        <div className="space-y-4" data-testid="leasing-settings-dialog">
            <div className="text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2">
                Zeitpunkte je Stufe. Positive Tage = vor Vertragsende, negative = nach Ende. Die E-Mail-Texte
                bearbeitest du je Stufe direkt über <b>„Vorlage bearbeiten"</b>. Alle erzeugten Mails laufen über die
                <b> zentrale E-Mail-Freigabe</b> (dort auch die Automatik je Mail).
            </div>

            <div className="flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-sm px-3 py-2.5">
                <Switch checked={!!t.leasing_auto_assign} onCheckedChange={(v) => set("leasing_auto_assign", v)} data-testid="leasing-autoassign-toggle" />
                <div className="flex-1">
                    <div className="text-sm font-bold text-[#111827]">Vorgänge automatisch zuweisen</div>
                    <div className="text-[11px] text-gray-500">Wie bei Finanzierungen: offene, unzugewiesene Vorgänge gehen an den anwesenden Verkäufer mit der geringsten Last.</div>
                </div>
                <Button size="sm" variant="outline" onClick={runNow} disabled={runBusy} className="h-8 rounded-sm text-xs font-bold shrink-0" data-testid="leasing-run-now">
                    {runBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Play className="w-3.5 h-3.5 mr-1" /> Jetzt prüfen & erzeugen</>}
                </Button>
            </div>

            <div className="border border-gray-200 rounded-sm px-3 py-3" data-testid="leasing-km-settings">
                <div className="text-sm font-black text-[#111827] mb-1">Mehr-/Minderkilometer-Abrechnung (KM-Leasing)</div>
                <div className="text-[11px] text-gray-500 mb-3">Gilt für Kilometer-Leasing bei der Rückgabe. Werte in <b>Netto-Cent pro km</b>. Bei 0 wird nichts berechnet.</div>
                <div className="grid grid-cols-2 gap-3">
                    <label className="text-[11px] text-gray-600 flex flex-col gap-1">
                        Toleranz (freie Mehr-/Minder-km)
                        <div className="inline-flex items-center gap-1">
                            <input type="number" min={0} step={100} value={t.km_free_allowance ?? ""} onChange={(e) => set("km_free_allowance", e.target.value)}
                                className="w-24 border border-gray-300 rounded-sm px-2 py-1 font-mono-num focus:border-[#111827] focus:outline-none" data-testid="leasing-km-free" />
                            <span>km</span>
                        </div>
                    </label>
                    <label className="text-[11px] text-gray-600 flex flex-col gap-1">
                        max. vergütete Minderkilometer
                        <div className="inline-flex items-center gap-1">
                            <input type="number" min={0} step={100} value={t.km_max_minder ?? ""} onChange={(e) => set("km_max_minder", e.target.value)}
                                className="w-24 border border-gray-300 rounded-sm px-2 py-1 font-mono-num focus:border-[#111827] focus:outline-none" data-testid="leasing-km-max-minder" />
                            <span>km</span>
                        </div>
                    </label>
                    <label className="text-[11px] text-gray-600 flex flex-col gap-1">
                        Nachberechnung je Mehrkilometer
                        <div className="inline-flex items-center gap-1">
                            <input type="number" min={0} step={0.1} value={t.km_mehr_cent ?? ""} onChange={(e) => set("km_mehr_cent", e.target.value)}
                                className="w-24 border border-gray-300 rounded-sm px-2 py-1 font-mono-num focus:border-[#111827] focus:outline-none" data-testid="leasing-km-mehr-cent" />
                            <span>ct/km</span>
                        </div>
                    </label>
                    <label className="text-[11px] text-gray-600 flex flex-col gap-1">
                        Vergütung je Minderkilometer
                        <div className="inline-flex items-center gap-1">
                            <input type="number" min={0} step={0.1} value={t.km_minder_cent ?? ""} onChange={(e) => set("km_minder_cent", e.target.value)}
                                className="w-24 border border-gray-300 rounded-sm px-2 py-1 font-mono-num focus:border-[#111827] focus:outline-none" data-testid="leasing-km-minder-cent" />
                            <span>ct/km</span>
                        </div>
                    </label>
                </div>
            </div>

            <div className="border border-gray-200 rounded-sm divide-y">
                {STAGES.map((s) => (
                    <div key={s.label} className="flex items-center justify-between gap-3 flex-wrap px-3 py-2.5" data-testid={`leasing-stage-${s.label}`}>
                        <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-gray-400" />
                            <div>
                                <div className="text-sm font-black text-[#111827]">{s.label}</div>
                                <div className="text-[11px] text-gray-500">{s.hint}</div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2">
                                {s.days.map((dk) => (
                                    <label key={dk} className="text-[11px] text-gray-600 inline-flex items-center gap-1">
                                        {DAY_LABEL[dk] && <span className="text-gray-400">{DAY_LABEL[dk]}</span>}
                                        <input type="number" min={-120} max={365} value={t[dk] ?? ""} onChange={(e) => set(dk, e.target.value)}
                                            className="w-16 border border-gray-300 rounded-sm px-2 py-1 font-mono-num focus:border-[#111827] focus:outline-none" data-testid={`leasing-days-${dk}`} />
                                        <span>Tage</span>
                                    </label>
                                ))}
                            </div>
                            <button type="button" onClick={() => onEditTemplate?.(s.tpl)}
                                className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#111827] underline decoration-[#FFCC00] decoration-2 hover:text-[#B8860B]"
                                data-testid={`leasing-edit-template-${s.tpl}`}>
                                Vorlage bearbeiten <ExternalLink className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <div className="flex justify-end">
                <Button onClick={save} disabled={busy} className="h-9 bg-[#111827] text-white rounded-sm text-xs font-bold" data-testid="leasing-settings-save">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
                </Button>
            </div>
        </div>
    );
}
